<template>
  <!-- Services filter — three legs that all narrow the same set of
       customer reservations:
         { services: Service[], employees: Employee[], date: { start, finish } }
       The matching logic and remote-params builder live on the filter
       config in `useCustomerFilters`; we only collect input here.

       The service/employee legs open as compact popover selectors so the
       customer sidebar stays scannable even with long catalogues. -->
  <FilterControlGroup>
    <!-- Date range — when both ends are blank the filter ignores it. -->
    <div class="space-y-1">
      <label class="text-xs font-medium text-gray-600 dark:text-gray-400">
        {{ $t('components.customer.list.fieldsWithDateRange.during') }}
      </label>
      <DateRangeFilter v-model="modelValue.date" />
    </div>

    <!-- Services list. `items` is array-of-arrays for groups; rows with
         `type: 'label'` render as the group header. `by` matches existing
         selections so re-opening the picker keeps the highlights. -->
    <div class="space-y-1">
      <div class="flex items-center justify-between">
        <label class="text-xs font-medium text-gray-600 dark:text-gray-400">
          {{ $t('general.services') }}
          <span v-if="modelValue.services.length" class="text-primary-500">
            ({{ modelValue.services.length }})
          </span>
        </label>
        <button
          v-if="modelValue.services.length"
          type="button"
          class="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          @click="clearServices"
        >
          {{ $t('general.clear') }}
        </button>
      </div>
      <ModelMultiSelect
        :model-value="modelValue.services"
        :items="serviceItems"
        :placeholder="$t('customer.filters.allServices')"
        by="idService"
        @update:model-value="(v: any) => onServicesChange(v)"
      />
    </div>

    <!-- Employees ("performed by"). Flat list, no grouping. -->
    <div class="space-y-1">
      <div class="flex items-center justify-between">
        <label class="text-xs font-medium text-gray-600 dark:text-gray-400">
          {{ $t('customer.filters.with') }}
          <span v-if="modelValue.employees.length" class="text-primary-500">
            ({{ modelValue.employees.length }})
          </span>
        </label>
        <button
          v-if="modelValue.employees.length"
          type="button"
          class="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          @click="clearEmployees"
        >
          {{ $t('general.clear') }}
        </button>
      </div>
      <ModelMultiSelect
        :model-value="modelValue.employees"
        :items="employeeItems"
        :placeholder="$t('customer.filters.allEmployees')"
        by="idEmployee"
        @update:model-value="(v: any) => onEmployeesChange(v)"
      />
    </div>
  </FilterControlGroup>
</template>

<script setup lang="ts">
import DateRangeFilter from './DateRangeFilter.vue'
import FilterControlGroup from './FilterControlGroup.vue'
import ModelMultiSelect from './ModelMultiSelect.vue'

// The filter's value shape needs to survive the popover lifecycle, which
// closes/reopens between interactions. `defineModel` gives us a writable
// proxy that emits back to the FilterController setValue path automatically.
const modelValue = defineModel<{
  services: any[]
  employees: any[]
  date: { start: number | null; finish: number | null }
}>({
  default: () => ({ services: [], employees: [], date: { start: null, finish: null } }),
})

const serviceStore = useServiceStore()
const employeeStore = useEmployeeStore()

// ---- Listbox items --------------------------------------------------------
//
// USelectMenu accepts either flat items or grouped item arrays. Rows with
// `type: 'label'` render as group headers, which is how we surface service
// categories. Each item carries the full underlying model so the emitted
// `update:model-value` payload is the original `Service` / `Employee`.

interface ListItem {
  label: string
  type?: 'label'
  idService?: number
  idEmployee?: number
  [key: string]: any
}

const serviceItems = computed<ListItem[][]>(() => {
  const categories = (serviceStore.categorised as any[]) ?? []
  return categories
    .filter((cat) => cat.services?.length)
    .map((cat) => {
      const group: ListItem[] = []
      if (cat.name) {
        group.push({ type: 'label', label: cat.name })
      }
      for (const service of cat.services) {
        group.push({ ...service, label: service.title })
      }
      return group
    })
})

const employeeItems = computed<ListItem[]>(() => {
  const employees = (employeeStore.all as any[]) ?? []
  return employees.map((emp: any) => ({ ...emp, label: emp.displayName }))
})

const onServicesChange = (v: any[]) => {
  modelValue.value = { ...modelValue.value, services: Array.isArray(v) ? v : [] }
}

const onEmployeesChange = (v: any[]) => {
  modelValue.value = { ...modelValue.value, employees: Array.isArray(v) ? v : [] }
}

const clearServices = () => {
  modelValue.value = { ...modelValue.value, services: [] }
}

const clearEmployees = () => {
  modelValue.value = { ...modelValue.value, employees: [] }
}

</script>
